import * as Sentry from '@sentry/react';
import { Address, UserRejectedRequestError } from 'viem';
import { BaseError } from 'wagmi';

import {
  DEFAULT_TRANSACTION_ERROR_MESS,
  ERROR_MESSAGES,
  GAS_LIMIT_TOO_HIGH_MESS,
  USER_REJECTED_TRANSACTION_MESS,
} from '@/app/shared/constants/errors';
import { handleContractsError } from '@/app/shared/lib/handleContractsError';

interface HandleTransactionErrorParams {
  error: unknown;
  action: string;
  isBatching: boolean;
  isNeedApprove?: boolean;
  account?: Address;
}

const getErrorText = (error: unknown): string => {
  if (!(error instanceof BaseError) && !(error && typeof error === 'object')) {
    return typeof error === 'string' ? error : '';
  }

  const base = error as BaseError & { details?: string; shortMessage?: string; message?: string };
  return [base.details, base.shortMessage, base.message, ...(base.metaMessages ?? [])]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join('\n');
};

const walkErrorChain = (error: unknown): unknown[] => {
  const chain: unknown[] = [];
  let current: unknown = error;
  const seen = new Set<unknown>();

  while (current && typeof current === 'object' && !seen.has(current)) {
    chain.push(current);
    seen.add(current);
    current = 'cause' in current ? (current as { cause?: unknown }).cause : undefined;
  }

  return chain;
};

export const isUserRejectedRequestError = (error: unknown): boolean => {
  for (const item of walkErrorChain(error)) {
    if (item instanceof UserRejectedRequestError) return true;

    if (item && typeof item === 'object') {
      const candidate = item as { name?: string; code?: number | string; shortMessage?: string; message?: string };
      if (candidate.name === 'UserRejectedRequestError') return true;
      if (candidate.code === 4001 || candidate.code === '4001') return true;

      const text = `${candidate.shortMessage ?? ''} ${candidate.message ?? ''}`.toLowerCase();
      if (
        text.includes('user rejected') ||
        text.includes('user denied') ||
        text.includes('rejected the request')
      ) {
        return true;
      }
    }
  }

  return false;
};

export const handleTransactionError = ({
  error,
  action,
  isBatching,
  isNeedApprove,
  account,
}: HandleTransactionErrorParams): string => {
  if (isUserRejectedRequestError(error)) {
    return USER_REJECTED_TRANSACTION_MESS;
  }

  const errorText = getErrorText(error);

  const details: string | undefined =
    error instanceof BaseError
      ? error.details
      : error && typeof error === 'object' && 'details' in error
        ? (error as { details: string }).details
        : undefined;

  const metaMessages: string[] | undefined = (error as BaseError)?.metaMessages;

  let parsedErrorMessage = DEFAULT_TRANSACTION_ERROR_MESS;

  if (/gas limit too high/i.test(errorText)) {
    parsedErrorMessage = GAS_LIMIT_TOO_HIGH_MESS;
  } else if (details) {
    parsedErrorMessage = handleContractsError(details, DEFAULT_TRANSACTION_ERROR_MESS);
  } else if (metaMessages) {
    const match: RegExpMatchArray | null = metaMessages[0]?.match(/Error:\s*(\w+)\(/);
    const errorName: string = match?.[1] ?? '';
    if (errorName) {
      parsedErrorMessage = ERROR_MESSAGES[errorName as keyof typeof ERROR_MESSAGES] ?? DEFAULT_TRANSACTION_ERROR_MESS;
    }
  } else if (error instanceof Error && error.message.trim()) {
    parsedErrorMessage = error.message;
  } else if (typeof error === 'string' && error.trim()) {
    parsedErrorMessage = error;
  }

  Sentry.captureException(error, {
    tags: {
      feature: 'transaction',
      action,
      isBatching,
    },
    contexts: {
      transaction: {
        details,
        metaMessages,
        errorMessage: parsedErrorMessage,
        isNeedApprove,
        account,
      },
    },
  });

  return parsedErrorMessage;
};
